import React, { useEffect, useState } from "react";
import { Layout, PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Eye, ShieldAlert, Users as UsersIcon, ScrollText, MessageSquare, ArrowLeft } from "lucide-react";

const TABS = [
  { key: "chats", label: "Conversaciones", icon: MessageSquare },
  { key: "reportes", label: "Reportes", icon: ShieldAlert },
  { key: "miembros", label: "Miembros", icon: UsersIcon },
  { key: "auditoria", label: "Auditoría de accesos", icon: ScrollText },
];

const Field = (props) => (
  <input {...props}
    className={`glass-field px-3 py-2 rounded-lg text-sm text-white placeholder-white/35 outline-none ${props.className || ""}`} />
);

export default function Conversations() {
  const [tab, setTab] = useState("chats");
  const [filters, setFilters] = useState({ user: "", q: "", date_from: "", date_to: "" });
  const [convs, setConvs] = useState(null);
  const [open, setOpen] = useState(null);
  const [logs, setLogs] = useState(null);
  const [reports, setReports] = useState(null);
  const [members, setMembers] = useState(null);
  const [stats, setStats] = useState(null);

  const search = async () => {
    setConvs(null);
    try {
      const { data } = await api.get("/admin/community/conversations", { params: filters });
      setConvs(data.conversations);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo buscar"); setConvs([]); }
  };

  const openConv = async (conv) => {
    setOpen({ loading: true, conv });
    try {
      const { data } = await api.get(`/admin/community/conversations/${conv.conv_id}/messages`,
        { params: { q: filters.q, date_from: filters.date_from, date_to: filters.date_to } });
      setOpen({ ...data, conv });
      toast.info("Este acceso quedó registrado en la auditoría");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo abrir"); setOpen(null); }
  };

  useEffect(() => {
    api.get("/admin/community/stats")
      .then(({ data }) => setStats(data))
      .catch((e) => console.warn("No se pudieron cargar las estadísticas:", e));
    search();
    // search sólo debe correr al entrar: los filtros se aplican con el botón Buscar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "auditoria" && !logs) api.get("/admin/community/access-logs").then(({ data }) => setLogs(data)).catch(() => setLogs([]));
    if (tab === "reportes" && !reports) api.get("/admin/community/reports").then(({ data }) => setReports(data)).catch(() => setReports([]));
    if (tab === "miembros" && !members) api.get("/admin/community/members").then(({ data }) => setMembers(data)).catch(() => setMembers([]));
  }, [tab, logs, reports, members]);

  const setReportStatus = async (id, status) => {
    try {
      await api.patch(`/admin/community/reports/${id}`, { status });
      setReports((r) => r.map((x) => (x.report_id === id ? { ...x, status } : x)));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const memberAction = async (key, action) => {
    try {
      const { data } = await api.post(`/admin/community/members/${key}/action`, { action });
      setMembers((m) => m.map((x) => (x.key === key ? { ...x, status: data.status } : x)));
      toast.success(action === "expulsar" ? "Miembro expulsado" : "Acceso restaurado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Layout>
      <PageHeader title="Ver conversaciones"
        subtitle="Acceso de administrador para seguimiento y prevención de fraude — cada consulta queda auditada" />
      <div className="p-8 space-y-5">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="community-stats">
            {[["Miembros", stats.members], ["Conversaciones", stats.conversations],
              ["Mensajes", stats.messages], ["Reportes abiertos", stats.reports_open],
              ["Expulsados", stats.expelled]].map(([k, v]) => (
              <div key={k} className="glass-card px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.14em] text-white/50">{k}</div>
                <div className="text-xl font-bold text-white">{v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-1 border-b border-white/10">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`conv-tab-${t.key}`} onClick={() => { setTab(t.key); setOpen(null); }}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-t-lg ${
                tab === t.key ? "text-white bg-white/8 border-b-2 border-red-500" : "text-white/55 hover:text-white"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "chats" && !open && (
          <>
            <div className="glass-card p-4 flex flex-wrap gap-2 items-end" data-testid="conv-filters">
              <Field data-testid="filter-user" placeholder="Usuario (correo, nombre o ID)" className="w-64"
                value={filters.user} onChange={(e) => setFilters({ ...filters, user: e.target.value })} />
              <Field data-testid="filter-q" placeholder="Palabra clave en los mensajes" className="w-64"
                value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
              <Field data-testid="filter-from" type="date" value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
              <Field data-testid="filter-to" type="date" value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
              <button data-testid="conv-search-btn" onClick={search}
                className="glass-btn glass-btn-red px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                <Search className="w-4 h-4" /> Buscar
              </button>
            </div>

            {convs === null ? <Loader2 className="w-6 h-6 animate-spin text-red-400" /> : (
              <div className="space-y-2" data-testid="conv-results">
                {convs.length === 0 && <div className="glass-card p-5 text-sm text-white/60">Sin resultados.</div>}
                {convs.map((c) => (
                  <button key={c.conv_id} data-testid={`conv-row-${c.conv_id}`} onClick={() => openConv(c)}
                    className="glass-card w-full text-left p-4 hover:border-red-500/40">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-white text-sm font-semibold truncate">{c.title}</div>
                        <div className="text-[11px] text-white/45 truncate">
                          {c.type} · {c.messages} mensajes
                          {c.matches !== null && c.matches !== undefined && ` · ${c.matches} coincidencias`}
                          {c.members?.length ? ` · ${c.members.map((m) => m.name).join(", ")}` : ""}
                        </div>
                      </div>
                      <Eye className="w-4 h-4 text-red-400 shrink-0" />
                    </div>
                    {c.last_message && (
                      <div className="text-[11px] text-white/40 mt-1 truncate">Último: {c.last_message.text}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "chats" && open && (
          <div className="glass-card p-5" data-testid="conv-viewer">
            <button data-testid="conv-back" onClick={() => setOpen(null)}
              className="flex items-center gap-2 text-xs text-white/60 hover:text-white mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> Volver a la búsqueda
            </button>
            {open.loading ? <Loader2 className="w-6 h-6 animate-spin text-red-400" /> : (
              <>
                <div className="text-white font-semibold">{open.conversation?.title}</div>
                <div className="text-[11px] text-red-300 mb-4">{open.notice}</div>
                <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-2">
                  {open.messages?.map((m) => {
                    const who = open.people?.find((p) => p.key === m.sender);
                    return (
                      <div key={m.msg_id} className="chat-bubble px-3 py-2" data-testid={`admin-msg-${m.msg_id}`}>
                        <div className="text-[11px] text-red-300/80 mb-0.5">
                          {who?.name || m.sender} · {(m.created_at || "").slice(0, 16).replace("T", " ")}
                          {m.deleted && " · eliminado"}
                        </div>
                        {m.text && <div className="text-sm text-white whitespace-pre-wrap">{m.text}</div>}
                        {m.media && (
                          <a className="text-xs text-red-300 underline"
                            href={`${process.env.REACT_APP_BACKEND_URL}${m.media.url}`}
                            target="_blank" rel="noreferrer">
                            {m.media.mime?.startsWith("image/") ? "Ver imagen" : m.media.mime?.startsWith("audio/") ? "Escuchar audio" : `Abrir ${m.media.name}`}
                          </a>
                        )}
                      </div>
                    );
                  })}
                  {open.messages?.length === 0 && <div className="text-sm text-white/60">Sin mensajes en ese filtro.</div>}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "reportes" && (
          reports === null ? <Loader2 className="w-6 h-6 animate-spin text-red-400" /> : (
            <div className="space-y-2" data-testid="reports-list">
              {reports.length === 0 && <div className="glass-card p-5 text-sm text-white/60">Sin reportes.</div>}
              {reports.map((r) => (
                <div key={r.report_id} className="glass-card p-4" data-testid={`report-${r.report_id}`}>
                  <div className="text-sm text-white">
                    {r.reporter_profile?.name || r.reporter} reportó a {r.target_profile?.name || r.target}
                  </div>
                  <div className="text-xs text-white/60 mt-1">{r.reason}</div>
                  <div className="text-[11px] text-white/40 mt-1">
                    {(r.created_at || "").slice(0, 16).replace("T", " ")} · {r.status}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`report-review-${r.report_id}`} onClick={() => setReportStatus(r.report_id, "REVISADO")}
                      className="glass-btn glass-btn-red px-3 py-1.5 rounded-lg text-xs">Marcar revisado</button>
                    <button data-testid={`report-dismiss-${r.report_id}`} onClick={() => setReportStatus(r.report_id, "DESCARTADO")}
                      className="glass-btn px-3 py-1.5 rounded-lg text-xs">Descartar</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "miembros" && (
          members === null ? <Loader2 className="w-6 h-6 animate-spin text-red-400" /> : (
            <div className="space-y-2" data-testid="members-list">
              {members.map((m) => (
                <div key={m.key} className="glass-card p-4 flex items-center justify-between gap-3"
                  data-testid={`admin-member-${m.key}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{m.name} <span className="text-white/40">· {m.email}</span></div>
                    <div className="text-[11px] text-white/45">
                      {m.status} · alta {(m.registered_at || "").slice(0, 10)}
                      {m.terms_accepted_at ? " · términos aceptados" : " · sin aceptar términos"}
                    </div>
                  </div>
                  {m.status === "EXPULSADO" ? (
                    <button data-testid={`restore-${m.key}`} onClick={() => memberAction(m.key, "restaurar")}
                      className="glass-btn px-3 py-1.5 rounded-lg text-xs shrink-0">Restaurar</button>
                  ) : (
                    <button data-testid={`expel-${m.key}`} onClick={() => memberAction(m.key, "expulsar")}
                      className="glass-btn glass-btn-red px-3 py-1.5 rounded-lg text-xs shrink-0">Expulsar</button>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {tab === "auditoria" && (
          logs === null ? <Loader2 className="w-6 h-6 animate-spin text-red-400" /> : (
            <div className="space-y-2" data-testid="access-logs">
              {logs.length === 0 && <div className="glass-card p-5 text-sm text-white/60">Sin accesos registrados.</div>}
              {logs.map((l) => (
                <div key={l.log_id} className="glass-card px-4 py-3 text-xs flex flex-wrap gap-2 justify-between"
                  data-testid={`log-${l.log_id}`}>
                  <span className="text-white/80">{l.admin_name} · {l.action} · {l.conv_id}</span>
                  <span className="text-white/45 font-mono">{(l.at || "").slice(0, 19).replace("T", " ")}</span>
                  {Object.keys(l.query || {}).length > 0 && (
                    <span className="text-white/40 w-full font-mono">{JSON.stringify(l.query)}</span>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </Layout>
  );
}
